// Types
import { AlertActionTriggerModel } from 'app/modules/webhooks/models';
import { AlertDetails } from 'app/modules/alerts/models';
import { AlertFiltersPayload } from 'app/modules/alerts/requests';
import { AlertResponseFast } from 'app/modules/alerts/types/responses';
import {
  AlertDetailsTab,
  AlertType,
  AssociatedAlert,
  FullAlertResponse,
} from 'app/modules/alerts/types';
import { CreateCaseLocationState } from 'app/modules/cases/models';
import {
  ArrayComparisonOperators,
  CustomDataFilters,
  SimpleComparisonOperators,
} from 'app/modules/filtersOld/models';
import { Filter } from 'app/modules/filters/models';
import { TabConfigType } from 'app/shared/CustomConfig/models';
import { U21ChipColor } from 'app/shared/u21-ui/components';
import { Unit21DataClassifier } from 'app/modules/dataSettings/responses';
import {
  ChainalysisFilters,
  CHAINALYSIS_FILTER_KEYS_SET,
} from 'app/modules/alerts/types/chainalysis';

// Constants
import {
  ALERT_FILTER_KEYS,
  NATIVE_ALERT_FILTER_KEYS_SET,
} from 'app/modules/alerts/filters';
import { FILTER_OPERATOR } from 'app/modules/filters/constants';

// Helpers
import { addDays, startOfDay } from 'date-fns';
import { getBEDateFormat } from 'app/shared/utils/timeHelpers';
import moment from 'moment';
import { cloneDeep, isEqual } from 'lodash';
import {
  AlertsByEntityExpensiveSorting,
  AlertsByEventExpensiveSorting,
  AlertsByObjectSorting,
  EXPENSIVE_ALERT_FILTERS,
  ExpensiveAlertFiltersType,
  GetAlertsGroupedByObjectExpensivePayload,
  GetAlertsGroupedByObjectPayload,
  RemovedExpensiveOperationsType,
} from 'app/modules/alerts/types/objectCentricInvestigation';
import { QueueAccessType } from 'app/modules/queues/models';
import { QueueDetailsResponse } from 'app/modules/queues/responses';

export const getAlertTypeForCustomTabs = (alertType?: AlertType | '') => {
  switch (alertType) {
    case AlertType.CHAINALYSIS:
      return TabConfigType.CHAINALYSIS_ALERT_TABS;
    case AlertType.WATCHLIST:
    case AlertType.ONBOARDING:
    case AlertType.EEL:
      return TabConfigType.WATCHLIST_ALERT_TABS;
    case AlertType.DARK_WEB:
      return TabConfigType.DARK_WEB_ALERT_TABS;
    case AlertType.FINCEN_314A:
      return TabConfigType.FINCEN_314A_ALERT_TABS;
    case AlertType.TRM:
      return TabConfigType.TRM_ALERT_TABS;
    default:
      return TabConfigType.TM_ALERT_TABS;
  }
};

export const getDefaultTabObject = (
  alertTabsByType: AlertDetailsTab[],
): Record<string, AlertDetailsTab> =>
  alertTabsByType.reduce(
    (acc, tab: AlertDetailsTab) => Object.assign(acc, { [tab.path]: tab }),
    {},
  );

export const generateCreateCaseLocationState = (
  alert: (AlertDetails | AlertResponseFast) &
    Pick<
      AlertDetails,
      | 'instruments'
      | 'entities'
      | 'events'
      | 'action_events'
      | 'txn_instrument_resolutions'
      | 'entity_resolutions'
      | 'event_resolutions'
      | 'action_event_resolutions'
    >,
  associatedAlerts: AssociatedAlert[],
  selectedAssociatedAlertIDs: number[] = [],
  assignee?: number,
): CreateCaseLocationState => {
  const associatedAlertsMap = associatedAlerts.reduce(
    (acc, i) => ({ ...acc, [i.id]: i }),
    {},
  );
  const selectedAssociatedAlerts = selectedAssociatedAlertIDs.map(
    (i) => associatedAlertsMap[i],
  );
  const alerts = [alert, ...selectedAssociatedAlerts];
  const associatedAlertEntities = selectedAssociatedAlerts.reduce(
    (acc, i) => [...acc, ...i.entity_external_ids],
    [],
  );
  const associatedAlertInstruments = selectedAssociatedAlerts.reduce(
    (acc, i) => [...acc, ...i.instrument_external_ids],
    [],
  );

  const alertEntitiesFiltered =
    alert.entities?.filter(
      (entity) => alert.entity_resolutions?.[entity.id] !== 'FALSE_POSITIVE',
    ) || [];
  const alertInstrumentsFiltered =
    alert.instruments?.filter(
      (instrument) =>
        alert.txn_instrument_resolutions?.[instrument.id] !== 'FALSE_POSITIVE',
    ) || [];

  const entities = [
    ...new Set([
      ...alertEntitiesFiltered.map((e) => e.external_id),
      ...associatedAlertEntities,
    ]),
  ];
  const instruments = [
    ...new Set([
      ...alertInstrumentsFiltered.map((i) => i.external_id),
      ...associatedAlertInstruments,
    ]),
  ];

  // associatedAlerts do not have events
  const events = alert.events
    ?.filter((e) => alert.event_resolutions[e.id] !== 'FALSE_POSITIVE')
    .map((e) => e.external_id);
  const actionEvents =
    alert?.action_events?.events
      ?.filter((a) => alert.action_event_resolutions[a.id] !== 'FALSE_POSITIVE')
      .map((a) => a.external_id) || [];
  return {
    originating_alert_id: alert.id,
    alerts,
    assignee,
    entities,
    events,
    actionEvents,
    instruments,
  };
};

export const getAlertQueueAlertCounterColor = (
  value: number,
  lowThreshold: number | null,
  highThreshold: number | null,
): U21ChipColor => {
  if (!lowThreshold && !highThreshold) return 'default';
  if (highThreshold && value > highThreshold) return 'error';
  if (lowThreshold && value > lowThreshold) return 'warning';
  return 'success';
};

const filterNumbers = (array: (number | string)[]): number[] => {
  return array.filter((i): i is number => typeof i === 'number');
};

const filterStrings = (array: (number | string)[]): string[] => {
  return array.filter((i): i is string => typeof i === 'string');
};

export const createAlertListFilters = (
  filters: Filter[],
): AlertFiltersPayload => {
  const { nativeFilters, customFilters, chainalysisFilters } = filters.reduce<{
    nativeFilters: Filter[];
    customFilters: Filter[];
    chainalysisFilters: Filter[];
  }>(
    (acc, i) => {
      if (NATIVE_ALERT_FILTER_KEYS_SET.has(i.key)) {
        acc.nativeFilters.push(i);
      } else if (CHAINALYSIS_FILTER_KEYS_SET.has(i.key)) {
        acc.chainalysisFilters.push(i);
      } else {
        acc.customFilters.push(i);
      }
      return acc;
    },
    {
      nativeFilters: [],
      customFilters: [],
      chainalysisFilters: [],
    },
  );

  const customDataFilters = customFilters.reduce<CustomDataFilters>(
    (acc, i) => {
      const CLASSIFIER_CUSTOM_DATA_KEY_MAPPING = {
        [Unit21DataClassifier.ENTITY]: 'entity',
        [Unit21DataClassifier.INSTRUMENT]: 'txn_instrument',
        [Unit21DataClassifier.EVENT]: 'event',
      };
      const { classifier, key } = JSON.parse(i.key);
      const customDataKey = CLASSIFIER_CUSTOM_DATA_KEY_MAPPING[classifier];
      if (!acc[customDataKey]) {
        acc[customDataKey] = [];
      }
      const { operator, value } = i;
      switch (operator) {
        case FILTER_OPERATOR.IS_GREATER_THAN_NUMBER:
          acc[customDataKey].push({
            key,
            nested: false,
            operator: SimpleComparisonOperators.GT,
            value,
          });
          break;

        case FILTER_OPERATOR.IS_LESS_THAN_NUMBER:
          acc[customDataKey].push({
            key,
            nested: false,
            operator: SimpleComparisonOperators.LT,
            value,
          });
          break;

        case FILTER_OPERATOR.IS_EXACT_TEXT:
          acc[customDataKey].push({
            key,
            nested: false,
            operator: SimpleComparisonOperators.EQ,
            value,
          });
          break;

        case FILTER_OPERATOR.IS_ONE_OF:
          acc[customDataKey].push({
            key,
            nested: false,
            operator: ArrayComparisonOperators.IN,
            value,
          });
          break;

        case FILTER_OPERATOR.IS_NOT_EXACT_TEXT:
          acc[customDataKey].push({
            key,
            nested: false,
            operator: SimpleComparisonOperators.NEQ,
            value,
          });
          break;

        case FILTER_OPERATOR.IS_NOT_ONE_OF:
          acc[customDataKey].push({
            key,
            nested: false,
            operator: ArrayComparisonOperators.NOT_IN,
            value,
          });
          break;

        case FILTER_OPERATOR.IS_EMPTY:
          acc[customDataKey].push({
            key,
            nested: false,
            operator: SimpleComparisonOperators.EQ,
            value: null,
          });
          break;

        case FILTER_OPERATOR.IS_NOT_EMPTY:
          acc[customDataKey].push({
            key,
            nested: false,
            operator: SimpleComparisonOperators.NEQ,
            value: null,
          });
          break;

        default:
          break;
      }

      return acc;
    },
    {},
  );

  // Process chainalysis filters to match custom_data_filters format
  const chainalysisFiltersObj = chainalysisFilters.reduce<ChainalysisFilters>(
    (acc, i) => {
      const { key, operator, value } = i;

      switch (operator) {
        case FILTER_OPERATOR.IS_EXACT_TEXT:
          acc.push({
            key,
            operator: SimpleComparisonOperators.EQ,
            value,
          });
          break;

        case FILTER_OPERATOR.IS_ONE_OF:
          // Ensure value is always an array for IS_ONE_OF operator
          acc.push({
            key,
            operator: ArrayComparisonOperators.IN,
            value,
          });
          break;

        case FILTER_OPERATOR.IS_NOT_EXACT_TEXT:
          acc.push({
            key,
            operator: SimpleComparisonOperators.NEQ,
            value,
          });
          break;

        case FILTER_OPERATOR.IS_NOT_ONE_OF:
          // Ensure value is always an array for IS_NOT_ONE_OF operator
          acc.push({
            key,
            operator: ArrayComparisonOperators.NOT_IN,
            value,
          });
          break;

        case FILTER_OPERATOR.IS_EMPTY:
          acc.push({
            key,
            operator: SimpleComparisonOperators.EQ,
            value: null,
          });
          break;

        case FILTER_OPERATOR.IS_NOT_EMPTY:
          acc.push({
            key,
            operator: SimpleComparisonOperators.NEQ,
            value: null,
          });
          break;
        default:
          break;
      }

      return acc;
    },
    [],
  );

  const nativeDataFilters = nativeFilters.reduce<AlertFiltersPayload>(
    (acc, i) => {
      const { key, operator, value } = i;
      switch (key) {
        case ALERT_FILTER_KEYS.AMOUNT: {
          if (
            operator === FILTER_OPERATOR.IS_GREATER_THAN_NUMBER &&
            value > 0
          ) {
            acc.minimum_amount = value;
          } else if (
            operator === FILTER_OPERATOR.IS_LESS_THAN_NUMBER &&
            value > 0
          ) {
            acc.maximum_amount = value;
          } else if (operator === FILTER_OPERATOR.IS_BETWEEN_NUMBER) {
            const [min, max] = value;
            if (min > 0) {
              acc.minimum_amount = min;
            }
            if (max > 0) {
              acc.maximum_amount = max;
            }
          }
          break;
        }

        case ALERT_FILTER_KEYS.ASSIGNEE: {
          if (operator === FILTER_OPERATOR.IS_ONE_OF) {
            acc.agent_ids = filterNumbers(value);
          }
          break;
        }

        case ALERT_FILTER_KEYS.CREATED_AT: {
          // note: this API is using the legacy date time format so using the old formatter which requires moment
          if (operator === FILTER_OPERATOR.IS_AFTER_DATE) {
            acc.created_start_date = getBEDateFormat(value, moment.ISO_8601);
          } else if (operator === FILTER_OPERATOR.IS_BEFORE_DATE) {
            acc.created_end_date = getBEDateFormat(
              startOfDay(addDays(new Date(value), 1)),
              moment.ISO_8601,
            );
          } else if (operator === FILTER_OPERATOR.IS_BETWEEN_DATE) {
            const [start, end] = value;
            acc.created_start_date = getBEDateFormat(start, moment.ISO_8601);
            acc.created_end_date = getBEDateFormat(
              startOfDay(addDays(new Date(end), 1)),
              moment.ISO_8601,
            );
          }
          break;
        }

        case ALERT_FILTER_KEYS.DISPOSITION: {
          if (operator === FILTER_OPERATOR.IS_ONE_OF) {
            acc.dispositions = filterStrings(value);
          }
          break;
        }

        case ALERT_FILTER_KEYS.DISPOSITIONED_AT: {
          // note: this API is using the legacy date time format so using the old formatter which requires moment
          if (operator === FILTER_OPERATOR.IS_AFTER_DATE) {
            acc.disposition_start_date = getBEDateFormat(
              value,
              moment.ISO_8601,
            );
          } else if (operator === FILTER_OPERATOR.IS_BEFORE_DATE) {
            acc.disposition_end_date = getBEDateFormat(
              startOfDay(addDays(new Date(value), 1)),
              moment.ISO_8601,
            );
          } else if (operator === FILTER_OPERATOR.IS_BETWEEN_DATE) {
            const [start, end] = value;
            acc.disposition_start_date = getBEDateFormat(
              start,
              moment.ISO_8601,
            );
            acc.disposition_end_date = getBEDateFormat(
              startOfDay(addDays(new Date(end), 1)),
              moment.ISO_8601,
            );
          }
          break;
        }

        case ALERT_FILTER_KEYS.ENTITY: {
          if (operator === FILTER_OPERATOR.IS_ONE_OF) {
            acc.entity_ids = value;
          }
          break;
        }

        case ALERT_FILTER_KEYS.ID_TITLE: {
          if (operator === FILTER_OPERATOR.CONTAINS_TEXT) {
            acc.phrase = value;
          }
          break;
        }

        case ALERT_FILTER_KEYS.INSTRUMENT: {
          if (operator === FILTER_OPERATOR.IS_ONE_OF) {
            acc.instrument_external_ids = filterStrings(value);
          }
          break;
        }

        case ALERT_FILTER_KEYS.RULE: {
          if (operator === FILTER_OPERATOR.IS_ONE_OF) {
            acc.rule_ids = filterNumbers(value);
          }
          break;
        }

        case ALERT_FILTER_KEYS.QUEUE: {
          if (operator === FILTER_OPERATOR.IS_ONE_OF) {
            acc.alert_queue_ids = filterNumbers(value);
          }
          break;
        }

        case ALERT_FILTER_KEYS.SOURCE: {
          if (operator === FILTER_OPERATOR.IS) {
            acc.sources = [String(value)];
          }
          break;
        }

        case ALERT_FILTER_KEYS.STATUS: {
          if (operator === FILTER_OPERATOR.IS_ONE_OF) {
            acc.statuses = filterStrings(value);
          }
          break;
        }

        case ALERT_FILTER_KEYS.SUBDISPOSITION: {
          if (operator === FILTER_OPERATOR.IS_ONE_OF) {
            acc.subdisposition_option_ids = filterNumbers(value);
          } else if (operator === FILTER_OPERATOR.IS_NOT_ONE_OF) {
            acc.is_not_subdisposition_option_ids = filterNumbers(value);
          }
          break;
        }

        case ALERT_FILTER_KEYS.TAG: {
          if (operator === FILTER_OPERATOR.IS_ONE_OF) {
            acc.tag_ids = filterNumbers(value);
          } else if (operator === FILTER_OPERATOR.IS_NOT_ONE_OF) {
            acc.is_not_tag_ids = filterNumbers(value);
          }
          break;
        }

        case ALERT_FILTER_KEYS.TYPE: {
          if (operator === FILTER_OPERATOR.IS_ONE_OF) {
            acc.alert_types = filterStrings(value);
          }
          break;
        }

        case ALERT_FILTER_KEYS.DISPOSITIONED_BY: {
          if (operator === FILTER_OPERATOR.IS_ONE_OF) {
            acc.dispositioned_by = filterNumbers(value);
          }
          break;
        }

        case ALERT_FILTER_KEYS.AI_RECOMMENDATION: {
          if (operator === FILTER_OPERATOR.IS_ONE_OF) {
            acc.ai_agent_recommendation = filterStrings(value);
          }
          break;
        }

        default:
          break;
      }
      return acc;
    },
    {},
  );

  return {
    ...nativeDataFilters,
    custom_data_filters:
      Object.keys(customDataFilters).length > 0 ? customDataFilters : undefined,
    chainalysis_filters:
      chainalysisFiltersObj.length > 0 ? chainalysisFiltersObj : undefined,
  };
};

export const filterAlertWorkflowButtons = (
  workflowButtons: AlertActionTriggerModel[],
  alert: Pick<FullAlertResponse, 'tags' | 'status' | 'alert_type' | 'queue_id'>,
  agentTeams: number[],
  removeDisplayTagLogic?: boolean,
): AlertActionTriggerModel[] => {
  const caseTagIDSet = new Set(alert.tags.map((i) => i.id));
  const agentTeamIDSet = new Set(agentTeams);
  return workflowButtons
    .filter((i) => {
      if (i.config.close_alert && alert.status === 'CLOSED') {
        return false;
      }

      if (i.config.alert_type && i.config.alert_type !== alert.alert_type) {
        return false;
      }

      if (
        i.config.show_on_alert_status &&
        i.config.show_on_alert_status !== alert.status
      ) {
        return false;
      }

      if (
        i.queues.length &&
        alert.queue_id &&
        !i.queues.includes(alert.queue_id)
      ) {
        return false;
      }
      return true;
    })
    .filter((i) => {
      if (!i.teams.length) {
        return true;
      }
      // show if workflow has a team the agent is a part of
      return i.teams.some((j) => agentTeamIDSet.has(j));
    })
    .filter((i) => {
      if (removeDisplayTagLogic) {
        return true;
      }
      // only show if case has all the workflow button display tags
      return i.display_tags.every((j) => caseTagIDSet.has(j));
    })
    .filter((i) => {
      // don't show if case has any of the workflow button masked tags
      return !i.masking_tags.some((j) => caseTagIDSet.has(j));
    });
};

export const getChecklistDisabledReason = (
  workflowButton: AlertActionTriggerModel,
  isChecklistValidated: boolean,
): string | undefined => {
  if (!workflowButton.config.enable_if_invalid && !isChecklistValidated) {
    return 'Complete checklist first';
  }
  return undefined;
};

const INITIAL_REMOVED_EXPENSIVE_OPERATIONS: RemovedExpensiveOperationsType = {
  sortColumn: undefined,
  filters: [],
};

export function extractBasicPayloadFromExpensive(
  expensivePayload: GetAlertsGroupedByObjectExpensivePayload,
): {
  basicPayload: GetAlertsGroupedByObjectPayload;
  removedExpensiveOperations: RemovedExpensiveOperationsType;
  shouldShowDisclaimer: boolean;
} {
  // Function will receive the whole payload and remove the unsupported expensive
  // filters/aggregates so we can use the storage endpoint with the payload
  const removedExpensiveOperations = cloneDeep(
    INITIAL_REMOVED_EXPENSIVE_OPERATIONS,
  );

  // Remove expensive sorting if it's not supported by the storage endpoint
  let basicSortBy = AlertsByObjectSorting.OPEN_ALERTS;
  if (
    Object.keys(AlertsByObjectSorting).includes(expensivePayload.sort_column)
  ) {
    // Expensive payload is sorting by a basic sorting option, no need to change it
    basicSortBy = expensivePayload.sort_column as AlertsByObjectSorting;
  } else {
    // If we are here it means that the sort column can not be of type AlertsByObjectSorting, casting since TS can't infer this
    removedExpensiveOperations.sortColumn = expensivePayload.sort_column as
      | AlertsByEntityExpensiveSorting
      | AlertsByEventExpensiveSorting;
  }

  // Remove expensive filters if they're not supported by the storage endpoint
  const filteredPayload = Object.keys(expensivePayload).reduce(
    (acc, key: ExpensiveAlertFiltersType) => {
      if (EXPENSIVE_ALERT_FILTERS.includes(key)) {
        if (expensivePayload[key] !== undefined && key !== 'sort_column') {
          // Sort column is tracked separately
          removedExpensiveOperations.filters.push(key);
        }
        return acc;
      }
      return {
        ...acc,
        [key]: expensivePayload[key],
      };
    },
    {} as Omit<
      GetAlertsGroupedByObjectExpensivePayload,
      ExpensiveAlertFiltersType
    >,
  );

  // Return the basic payload and the removed expensive operations
  return {
    basicPayload: {
      ...filteredPayload,
      sort_column: basicSortBy,
    },
    removedExpensiveOperations,
    shouldShowDisclaimer: !isEqual(
      removedExpensiveOperations,
      INITIAL_REMOVED_EXPENSIVE_OPERATIONS,
    ),
  };
}

export const isQueueAssignedToAgent = (
  queue: QueueDetailsResponse,
  agentTeamIDSet: Set<number>,
): boolean => {
  return (
    queue.org_default_queue ||
    (queue.teams.length === 0 && queue.access_type === QueueAccessType.OPEN) ||
    queue.teams.some((i) => agentTeamIDSet.has(i.id))
  );
};
